import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  onSnapshot,
  QuerySnapshot,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { Employer, PreFundingStatus } from '@/models/employer.model';
import {
  auditCreate,
  auditDelete,
  auditStatusChange,
  auditView,
  auditListQuery,
} from '@/utils/audit-integration';
import { AuditEntityType } from '@/models/audit-log.model';

const EMPLOYERS_COLLECTION = 'employers';
const ADMIN_EMPLOYERS_INDEX_COLLECTION = 'admin_index_employers';

export class EmployerService {
  /**
   * Get all employers
   */
  static async getAllEmployers(): Promise<Employer[]> {
    const q = query(
      collection(db, EMPLOYERS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const employers = snapshot.docs.map(doc => this.docToEmployer(doc.id, doc.data()));

    auditListQuery(AuditEntityType.employer, {
      queryName: 'all_employers',
      resultCount: employers.length,
    }).catch(console.error);

    return employers;
  }

  /**
   * Get employer by ID
   */
  static async getEmployerById(id: string, logAccess: boolean = false): Promise<Employer | null> {
    const docRef = doc(db, EMPLOYERS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    const employer = this.docToEmployer(docSnap.id, docSnap.data());
    
    // Log access if requested (for detailed audit trails)
    if (logAccess) {
      auditView(AuditEntityType.employer, id, employer.name).catch(console.error);
    }
    
    return employer;
  }

  /**
   * Subscribe to employers changes
   */
  static subscribeToEmployers(
    callback: (employers: Employer[]) => void
  ): () => void {
    const q = query(
      collection(db, EMPLOYERS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    
    return onSnapshot(q, (snapshot: QuerySnapshot) => {
      const employers = snapshot.docs.map(doc =>
        this.docToEmployer(doc.id, doc.data())
      );
      callback(employers);
    });
  }

  /**
   * Search employers by name
   */
  static async searchEmployers(searchTerm: string): Promise<Employer[]> {
    const allEmployers = await this.getAllEmployers();
    const lowerSearch = searchTerm.toLowerCase();
    
    const filtered = allEmployers.filter(employer =>
      employer.name.toLowerCase().includes(lowerSearch) ||
      employer.description?.toLowerCase().includes(lowerSearch)
    );
    
    auditListQuery(AuditEntityType.employer, {
      queryName: 'search_employers',
      resultCount: filtered.length,
      filters: { searchTerm },
    }).catch(console.error);

    return filtered;
  }

  /**
   * Filter employers by status (uses admin index for performance)
   */
  static async filterEmployersByStatus(isActive: boolean): Promise<Employer[]> {
    // Use admin index for efficient querying
    const q = query(
      collection(db, ADMIN_EMPLOYERS_INDEX_COLLECTION),
      where('isActive', '==', isActive),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const employers: Employer[] = [];
    
    // Fetch full employer documents from index references
    for (const docSnap of snapshot.docs) {
      const indexData = docSnap.data();
      const employerId = indexData.employerId || docSnap.id;
      
      try {
        const employerDoc = await getDoc(doc(db, EMPLOYERS_COLLECTION, employerId));
        if (employerDoc.exists()) {
          employers.push(this.docToEmployer(employerDoc.id, employerDoc.data()));
        }
      } catch (error) {
        console.error(`Error fetching employer ${employerId}:`, error);
      }
    }

    auditListQuery(AuditEntityType.employer, {
      queryName: 'filter_employers_by_status',
      resultCount: employers.length,
      filters: { isActive },
    }).catch(console.error);

    return employers;
  }

  /**
   * Create employer
   */
  static async createEmployer(employerData: Partial<Employer>): Promise<string> {
    const data: any = {
      name: employerData.name || '',
      description: employerData.description || null,
      adminUserIds: employerData.adminUserIds || [],
      totalMembers: employerData.totalMembers || 0,
      benefitPlanIds: employerData.benefitPlanIds || [],
      isActive: employerData.isActive ?? true,
      stripeCustomerId: employerData.stripeCustomerId || null,
      paymentMethodId: employerData.paymentMethodId || null,
      paymentMethodVerified: employerData.paymentMethodVerified ?? false,
      paymentMethodAddedAt: employerData.paymentMethodAddedAt
        ? Timestamp.fromDate(employerData.paymentMethodAddedAt)
        : null,
      monthlyBudgetLimit: employerData.monthlyBudgetLimit || null,
      preFundingStatus: employerData.preFundingStatus ?? PreFundingStatus.none,
      preFundedBalance: employerData.preFundedBalance || null,
      preFundingExpiresAt: employerData.preFundingExpiresAt
        ? Timestamp.fromDate(employerData.preFundingExpiresAt)
        : null,
      preFundingDiscountPercent: employerData.preFundingDiscountPercent ?? 0.0,
      stopLossPolicyId: employerData.stopLossPolicyId || null,
      hasCobraCoverage: employerData.hasCobraCoverage ?? false,
      spdConfigurationId: employerData.spdConfigurationId || null,
      lastComplianceCheck: employerData.lastComplianceCheck
        ? Timestamp.fromDate(employerData.lastComplianceCheck)
        : null,
      createdAt: Timestamp.now(),
      lastUpdated: Timestamp.now(),
    };

    const docRef = await addDoc(collection(db, EMPLOYERS_COLLECTION), data);
    
    // Note: Firestore trigger will also log this, but explicit logging provides more context
    auditCreate(AuditEntityType.employer, docRef.id, employerData.name, {
      details: { adminCount: employerData.adminUserIds?.length || 0 },
    }).catch(console.error);
    
    return docRef.id;
  }

  /**
   * Update employer
   */
  static async updateEmployer(
    employerId: string,
    updates: Partial<Employer>
  ): Promise<void> {
    const docRef = doc(db, EMPLOYERS_COLLECTION, employerId);
    const updateData: any = {
      lastUpdated: Timestamp.now(),
    };

    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.adminUserIds !== undefined) updateData.adminUserIds = updates.adminUserIds;
    if (updates.totalMembers !== undefined) updateData.totalMembers = updates.totalMembers;
    if (updates.benefitPlanIds !== undefined) updateData.benefitPlanIds = updates.benefitPlanIds;
    if (updates.isActive !== undefined) updateData.isActive = updates.isActive;
    if (updates.stripeCustomerId !== undefined) updateData.stripeCustomerId = updates.stripeCustomerId;
    if (updates.paymentMethodId !== undefined) updateData.paymentMethodId = updates.paymentMethodId;
    if (updates.paymentMethodVerified !== undefined) updateData.paymentMethodVerified = updates.paymentMethodVerified;
    if (updates.monthlyBudgetLimit !== undefined) updateData.monthlyBudgetLimit = updates.monthlyBudgetLimit;
    if (updates.preFundingStatus !== undefined) updateData.preFundingStatus = updates.preFundingStatus;
    if (updates.preFundedBalance !== undefined) updateData.preFundedBalance = updates.preFundedBalance;
    if (updates.preFundingExpiresAt !== undefined) {
      updateData.preFundingExpiresAt = updates.preFundingExpiresAt
        ? Timestamp.fromDate(updates.preFundingExpiresAt)
        : null;
    }
    if (updates.preFundingDiscountPercent !== undefined) {
      updateData.preFundingDiscountPercent = updates.preFundingDiscountPercent;
    }
    if (updates.stopLossPolicyId !== undefined) updateData.stopLossPolicyId = updates.stopLossPolicyId;
    if (updates.hasCobraCoverage !== undefined) updateData.hasCobraCoverage = updates.hasCobraCoverage;
    if (updates.spdConfigurationId !== undefined) updateData.spdConfigurationId = updates.spdConfigurationId;
    if (updates.lastComplianceCheck !== undefined) {
      updateData.lastComplianceCheck = updates.lastComplianceCheck
        ? Timestamp.fromDate(updates.lastComplianceCheck)
        : null;
    }

    await updateDoc(docRef, updateData);
  }

  /**
   * Toggle employer active status
   */
  static async toggleEmployerStatus(
    employerId: string,
    isActive: boolean
  ): Promise<void> {
    const employer = await this.getEmployerById(employerId);
    await this.updateEmployer(employerId, { isActive: !isActive });
    
    // Log status change
    auditStatusChange(
      AuditEntityType.employer,
      employerId,
      employer?.name,
      isActive ? 'active' : 'inactive',
      !isActive ? 'active' : 'inactive'
    ).catch(console.error);
  }

  /**
   * Delete employer
   */
  static async deleteEmployer(employerId: string): Promise<void> {
    const employer = await this.getEmployerById(employerId);
    const docRef = doc(db, EMPLOYERS_COLLECTION, employerId);
    await deleteDoc(docRef);
    
    // Note: Firestore trigger will also log this
    auditDelete(AuditEntityType.employer, employerId, employer?.name).catch(console.error);
  }

  /**
   * Add admin to employer
   */
  static async addAdmin(employerId: string, userId: string): Promise<void> {
    const employer = await this.getEmployerById(employerId);
    if (!employer) throw new Error('Employer not found');

    const updatedAdminIds = [...employer.adminUserIds];
    if (!updatedAdminIds.includes(userId)) {
      updatedAdminIds.push(userId);
      await this.updateEmployer(employerId, { adminUserIds: updatedAdminIds });
    }
  }

  /**
   * Remove admin from employer
   */
  static async removeAdmin(employerId: string, userId: string): Promise<void> {
    const employer = await this.getEmployerById(employerId);
    if (!employer) throw new Error('Employer not found');

    const updatedAdminIds = employer.adminUserIds.filter(id => id !== userId);
    await this.updateEmployer(employerId, { adminUserIds: updatedAdminIds });
  }

  /**
   * Convert Firestore document to Employer
   */
  private static docToEmployer(id: string, data: any): Employer {
    return {
      id,
      name: data.name || '',
      description: data.description,
      adminUserIds: data.adminUserIds || [],
      totalMembers: data.totalMembers || 0,
      benefitPlanIds: data.benefitPlanIds || [],
      isActive: data.isActive ?? true,
      stripeCustomerId: data.stripeCustomerId,
      paymentMethodId: data.paymentMethodId,
      paymentMethodVerified: data.paymentMethodVerified ?? false,
      paymentMethodAddedAt: data.paymentMethodAddedAt?.toDate(),
      monthlyBudgetLimit: data.monthlyBudgetLimit,
      preFundingStatus: data.preFundingStatus ?? PreFundingStatus.none,
      preFundedBalance: data.preFundedBalance,
      preFundingExpiresAt: data.preFundingExpiresAt?.toDate(),
      preFundingDiscountPercent: data.preFundingDiscountPercent ?? 0.0,
      stopLossPolicyId: data.stopLossPolicyId,
      hasCobraCoverage: data.hasCobraCoverage ?? false,
      spdConfigurationId: data.spdConfigurationId,
      lastComplianceCheck: data.lastComplianceCheck?.toDate(),
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }
}

